import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const IMG_SETUP =
  "https://images.unsplash.com/photo-1733860398124-5bb9422ad21a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxjb2ZmZWUlMjBjYXRlcmluZyUyMHNldHVwfGVufDB8fHx8MTc4NzgxMjMyOXww&ixlib=rb-4.1.0&q=85&w=1200&auto=format&fit=crop";
const IMG_PLATTERS =
  "https://images.unsplash.com/photo-1766773443039-b4d053213bf6?fm=jpg&q=80&w=1200&auto=format&fit=crop";
const IMG_EVENING =
  "https://images.unsplash.com/photo-1678261739137-85881474209c?fm=jpg&q=80&w=1200&auto=format&fit=crop";

const cards = [
  {
    id: "offering-espresso-cart",
    tag: "The classic",
    title: "Espresso Cart",
    body: "A full café bar on wheels — espresso, lattes, cappuccinos and hot chocolate, served by our baristas for up to 300 guests.",
    img: IMG_SETUP,
    alt: "Espresso cart hire in Cyprus — barista brewing at a Brew & Co. mobile coffee bar",
    cls: "md:col-span-5 md:row-span-2",
    aspect: "aspect-[4/5] md:aspect-auto md:h-full",
  },
  {
    id: "offering-coffee-platters",
    tag: "Coffee & bites",
    title: "Coffee, Snacks & Platters",
    body: "The bar plus the table — pair our coffee with pastry platters, snack boards and sweet bites, so drinks and nibbles are sorted together.",
    img: IMG_PLATTERS,
    alt: "Coffee and pastry platters catered by Brew & Co. for events in Cyprus",
    cls: "md:col-span-7",
    aspect: "aspect-[16/9]",
  },
  {
    id: "offering-evening-bar",
    tag: "After dark",
    title: "Evening Cocktail Bar",
    body: "For parties that run past sunset — espresso martinis, coffee cocktails and spirited pours, shaken at your evening event.",
    img: IMG_EVENING,
    alt: "Espresso martinis served at a Brew & Co. evening cocktail bar in Cyprus",
    cls: "md:col-span-4",
    aspect: "aspect-[4/3]",
  },
];

export default function Offerings() {
  return (
    <section id="offerings" data-testid="offerings-section" className="py-24 lg:py-32 bg-taupe/25 border-y border-foreground/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-16 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-matcha mb-4">What we offer</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl" data-testid="offerings-heading">
              Three bars, one promise — <em className="italic text-matcha">really good coffee</em>
            </h2>
          </div>
          <p className="md:col-span-4 text-sm leading-relaxed text-espresso/60">
            From wedding coffee catering to corporate coffee catering across Cyprus, every package includes setup, breakdown, our baristas, cups and napkins. Custom menus and latte-art moments happily arranged.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:auto-rows-[280px]">
          {cards.map((c, i) => (
            <motion.article
              key={c.id}
              data-testid={c.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.08 * i }}
              className={`${c.cls} group relative overflow-hidden rounded-[2rem] border border-foreground/10 bg-alabaster flex flex-col`}
            >
              <div className={`overflow-hidden ${c.aspect} ${c.cls.includes("row-span-2") ? "md:flex-1" : ""}`}>
                <img src={c.img} alt={c.alt} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
              <div className="p-7">
                <p className="font-mono text-[10px] uppercase tracking-widest text-matcha mb-2">{c.tag}</p>
                <h3 className="text-2xl mb-2">{c.title}</h3>
                <p className="text-sm leading-relaxed text-espresso/65">{c.body}</p>
              </div>
            </motion.article>
          ))}

          <motion.a
            href="#contact"
            data-testid="offering-bespoke-cta"
            onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.24 }}
            className="md:col-span-3 group rounded-[2rem] bg-matcha text-alabaster p-7 flex flex-col justify-between hover:bg-espresso transition-colors duration-500"
          >
            <ArrowUpRight size={22} className="self-end transition-transform duration-500 group-hover:rotate-45" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest opacity-80 mb-2">Something else?</p>
              <h3 className="text-2xl leading-snug">Dream up a bespoke menu with us</h3>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
